import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Shield, RefreshCw, GripVertical } from 'lucide-react';
import { adminManagementApi } from '../../services/api';

/**
 * Security Overview — the data-model + granting-rules reference.
 * Aimed at SysAdmins and future AppAdmins. Read this before granting.
 *
 * Rewritten 2026-08-07 for the a20.044 permission-model rework:
 * role is bundling-only, no reports backdoor, supportingdoc split,
 * datasheet keys as frontend-only gates. STUI-only — no inline CSS.
 */
const SecurityOverviewPage = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminManagementApi.getSummary();
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to load summary:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="stui-module-loading">Loading...</div>;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <Shield size={20} />
          <h1>Security Overview</h1>
          <span className="stui-module-header-meta">
            {summary
              ? `${summary.activeUsers} users • ${summary.databases} databases • ${summary.permissions} permissions`
              : ''}
          </span>
        </div>
        <div className="stui-module-header-actions">
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions">
              <GripVertical size={16} />
            </button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); load(); }}>
                  <RefreshCw size={16} /><span>Refresh</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="stui-detail-content">
        <div className="stui-preview-zone">
          <h1>PMD Security Model</h1>
          <p>
            Read this page before granting anything. The rules are small
            but strict. If you're about to click "Grant" and you're not
            sure why, stop and walk through the resolution below.
          </p>
          <p>
            <strong>Rule of thumb (a20.044):</strong> role does nothing
            except bundle DefRole grants. Every capability is an explicit
            permission key. Nothing is hidden in role logic. The one
            exception is the <code>IsSysAdmin</code> flag on
            <code> AppSec_Users</code>, which bypasses every check.
          </p>

          {/* ── Entities ───────────────────────────────── */}
          <h2 className="stui-detail-section-header">Entities</h2>
          <p>Everything downstream references these. Names in <code>code</code> are the AppSec tables.</p>
          <table className="stui-data-grid">
            <thead>
              <tr><th>Entity</th><th>Table</th><th>What it is</th></tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>User</strong></td>
                <td><code>AppSec_Users</code></td>
                <td>Human account. Signed in via Entra ID. Has an <code>IsSysAdmin</code> flag — SysAdmins bypass every permission check server-side. The flag is the sole bypass mechanism.</td>
              </tr>
              <tr>
                <td><strong>Organization</strong></td>
                <td><code>AppSec_Organizations</code></td>
                <td>Company grouping (Wunderlich-Malec, client orgs, etc.). Users belong to one Org. Cosmetic today; may drive tenancy later.</td>
              </tr>
              <tr>
                <td><strong>Database</strong></td>
                <td><code>AppSec_Databases</code></td>
                <td>Project DB instance — GWA01, GFS02, PNA01, TMPLT, etc. Each has a <code>Slug</code> — the identity string used in URLs, roles, and permissions.</td>
              </tr>
              <tr>
                <td><strong>Role</strong></td>
                <td><code>AppSec_UserRoles</code></td>
                <td>A user's role in a specific database. Values: Guest, User, PowerUser, AppAdmin. <strong>Role is a bundling shortcut for DefRole grants — not an enforcement gate.</strong> SysAdmin is a user-level flag, not a per-DB role.</td>
              </tr>
              <tr>
                <td><strong>Permission Key</strong></td>
                <td><code>AppSec_DefPermissions</code></td>
                <td>Every gated feature/table/action has one. Pattern: <code>&lt;section&gt;.&lt;feature&gt;</code>. Source of truth — every reference FKs back to this table.</td>
              </tr>
              <tr>
                <td><strong>Role Default</strong></td>
                <td><code>AppSec_DefRoles</code></td>
                <td>Maps role → permission grants. Grant-only (no explicit denies). When a user's role is assigned, they inherit these grants for that DB. Every default grant is explicit and visible on the Permission Defaults page.</td>
              </tr>
              <tr>
                <td><strong>User Override</strong></td>
                <td><code>AppSec_UserPermissions</code></td>
                <td>Explicit Grant or Deny for a specific (user, DB, permission). Overrides the role default. Use sparingly.</td>
              </tr>
              <tr>
                <td><strong>Global Grant</strong></td>
                <td><code>AppSec_GlobalPermissions</code></td>
                <td>Cross-DB grant per user. For permissions not tied to a specific project DB (e.g. <code>admin.main</code>, <code>development.main</code>). Rare — mostly for ops/sysadmins.</td>
              </tr>
              <tr>
                <td><strong>Table Owner</strong></td>
                <td><code>AppSec_TableOwner</code></td>
                <td>Classifies every project-DB table with an owner permission key + category (engineering | dataentry). DynamicController consults this on every CRUD.</td>
              </tr>
            </tbody>
          </table>

          {/* ── Permission naming ──────────────────────── */}
          <h2 className="stui-detail-section-header">Permission Naming</h2>
          <p>
            Flat <code>&lt;section&gt;.&lt;feature&gt;</code>. Section prefix matches
            the <code>Category</code> column 1:1. No <code>module.*</code> or
            <code> feature.*</code> wrapper prefixes.
          </p>
          <table className="stui-data-grid">
            <thead>
              <tr><th>Section</th><th>What it gates</th><th>Examples</th></tr>
            </thead>
            <tbody>
              <tr><td><code>action</code></td>       <td>Verb-level gates (edit/delete/download)</td><td><code>action.engineering.listview.update</code>, <code>action.engineering.datasheet.update</code>, <code>action.bulkdelete</code>, <code>action.downloaddata</code></td></tr>
              <tr><td><code>admin</code></td>        <td>Administration nav + sub-pages</td><td><code>admin.main</code>, <code>admin.users</code></td></tr>
              <tr><td><code>dataentry</code></td>    <td>Data Entry module nav</td><td><code>dataentry.commissioning</code>, <code>dataentry.materialtransfer</code></td></tr>
              <tr><td><code>development</code></td>  <td>Dev tools nav</td><td><code>development.main</code></td></tr>
              <tr><td><code>engineering</code></td>  <td>Engineering data CRUD scopes</td><td><code>engineering.assets</code>, <code>engineering.supportingdoc</code>, <code>engineering.supportingdoc.view</code></td></tr>
              <tr><td><code>integration</code></td>  <td>Integration tools</td><td><code>integration.udtcomment</code>, <code>integration.plcplatforms</code></td></tr>
              <tr><td><code>projectmanagement</code></td><td>PM subsections</td><td><code>projectmanagement.finance</code>, <code>projectmanagement.transmittals</code></td></tr>
              <tr><td><code>reports</code></td>      <td>Reports section headers (nav-derived)</td><td><code>reports.general</code>, <code>reports.commissioning</code></td></tr>
              <tr><td><code>utilities</code></td>    <td>Utilities tools</td><td><code>utilities.bulkimport</code>, <code>utilities.audittrailcrud</code></td></tr>
            </tbody>
          </table>

          {/* ── CRUD check ─────────────────────────────── */}
          <h2 className="stui-detail-section-header">The CRUD Check</h2>
          <p>
            Every DynamicController read/write/delete runs the same check.
            No hidden role-tier shortcuts, no cross-cutting backdoors.
          </p>
          <table className="stui-data-grid">
            <thead>
              <tr><th>Verb</th><th>Requires</th></tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Read</strong></td>
                <td>Owner permission for the table (from <code>AppSec_TableOwner</code>).<br />
                <em>Special case:</em> tables owned by <code>engineering.supportingdoc</code> accept either the base key <em>or</em> <code>engineering.supportingdoc.view</code>.</td>
              </tr>
              <tr>
                <td><strong>Create / Update (listview)</strong></td>
                <td>Owner permission <strong>AND</strong> <code>action.&lt;category&gt;.listview.update</code>.<br />
                <em>Special case:</em> supportingdoc writes require the <em>base</em> key. The <code>.view</code> variant is read-only.</td>
              </tr>
              <tr>
                <td><strong>Create / Update (datasheet bulk-edit pencil)</strong></td>
                <td>Frontend-only gate on <code>action.&lt;category&gt;.datasheet.update</code>. The backend enforces the same <code>action.&lt;category&gt;.listview.update</code> for both listview and datasheet writes — the datasheet key is a UX gate for the pencil button, not an API gate.</td>
              </tr>
              <tr>
                <td><strong>Delete (listview single row)</strong></td>
                <td>Owner permission <strong>AND</strong> <code>action.&lt;category&gt;.listview.delete</code>.</td>
              </tr>
              <tr>
                <td><strong>Bulk delete (any view)</strong></td>
                <td>Owner permission <strong>AND</strong> <code>action.bulkdelete</code>. Universal high-risk gate — same rule for listview and datasheet bulk ops.</td>
              </tr>
              <tr>
                <td><strong>Download</strong></td>
                <td><code>action.downloaddata</code>. No owner check here — module gating is upstream at the nav level.</td>
              </tr>
            </tbody>
          </table>

          {/* ── Resolution order ───────────────────────── */}
          <h2 className="stui-detail-section-header">Permission Resolution Order</h2>
          <p>When the app asks "can this user do X?" — the answer walks this ladder in order. First match wins.</p>
          <ol>
            <li>Is user <strong>SysAdmin</strong>? → <strong>allow everything</strong> (server-side flag bypass, no further checks).</li>
            <li>Is there an explicit row in <code>AppSec_UserPermissions</code> for (user, DB, permission)? → use that Grant/Deny.</li>
            <li>Does <code>AppSec_DefRoles</code> grant this permission to the user's role? → allow.</li>
            <li>Does <code>AppSec_GlobalPermissions</code> grant this permission to the user across DBs? → allow.</li>
            <li>No match → <strong>deny</strong>.</li>
          </ol>
          <p>
            <strong>What's NOT in this ladder anymore:</strong> the old
            "AppAdmin on target DB → allow everything" shortcut is gone.
            AppAdmins get their permissions via explicit DefRole grants
            (41 keys by default) — the same mechanism as every other
            role. The <code>reports.*</code> universal-read backdoor is
            also gone.
          </p>

          {/* ── Default grants by role ─────────────────── */}
          <h2 className="stui-detail-section-header">Default Grants by Role</h2>
          <p>
            Full snapshot of the a20.044 <code>AppSec_DefRoles</code>
            state. See <strong>Administration &gt; Security &gt; Permission Defaults</strong>
            for the live editable view.
          </p>
          <table className="stui-data-grid">
            <thead>
              <tr><th>Role</th><th>Count</th><th>What they get</th></tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Guest</strong></td>
                <td>5</td>
                <td>The 5 non-supportingdoc engineering owner keys: <code>engineering.assets</code>, <code>engineering.drawings</code>, <code>engineering.networking</code>, <code>engineering.powerdist</code>, <code>engineering.soo</code>. Read-only baseline for a DB grant.</td>
              </tr>
              <tr>
                <td><strong>User</strong></td>
                <td>15</td>
                <td>Guest grants + <code>engineering.supportingdoc.view</code> + listview update action keys (engineering + dataentry) + <code>action.downloaddata</code> + all 6 <code>reports.*</code> section keys. No delete keys.</td>
              </tr>
              <tr>
                <td><strong>PowerUser</strong></td>
                <td>32</td>
                <td>User grants + <code>engineering.supportingdoc</code> base (upgrades view → edit-eligible) + listview delete keys + datasheet update keys + <code>action.bulkdelete</code> + all utilities + all integration tools.</td>
              </tr>
              <tr>
                <td><strong>AppAdmin</strong></td>
                <td>41</td>
                <td>PowerUser grants + all 8 dataentry module keys + <code>admin.users</code>. Essentially "everything in their DB" but achieved through explicit grants, not a hidden bypass.</td>
              </tr>
              <tr>
                <td><strong>SysAdmin</strong></td>
                <td>0</td>
                <td>Zero DefRoles rows. The <code>IsSysAdmin</code> flag on <code>AppSec_Users</code> is the sole bypass mechanism — every server-side check short-circuits on the flag. Displayed on the Permission Defaults page as a synthetic always-Grant column (labeled "SysAdmin (flag)") so admins can see the full picture.</td>
              </tr>
            </tbody>
          </table>

          {/* ── Rules for granting ─────────────────────── */}
          <h2 className="stui-detail-section-header">Rules for Granting — Read Before Clicking Grant</h2>
          <table className="stui-data-grid">
            <thead>
              <tr><th>Rule</th><th>Why</th></tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Prefer role defaults</strong> over per-user grants.</td>
                <td>If a permission logically belongs to a role (all PowerUsers should have it), add it to <code>AppSec_DefRoles</code>. Per-user grants are for exceptions only — otherwise you build a maintenance mess.</td>
              </tr>
              <tr>
                <td><strong>Deny is per-user only.</strong></td>
                <td>Role defaults are Grant-only. If you need to revoke one permission from one person, use <code>AppSec_UserPermissions</code> with AccessType=Deny.</td>
              </tr>
              <tr>
                <td><strong>Global grants only for non-DB permissions.</strong></td>
                <td>Use <code>AppSec_GlobalPermissions</code> for permissions that don't tie to a project DB (<code>admin.main</code>, <code>development.main</code>). Never use it as a shortcut to grant a per-DB permission across all DBs — that pattern hides intent.</td>
              </tr>
              <tr>
                <td><strong>Never delete a permission that has grants.</strong></td>
                <td><code>FK_TableOwner</code> is <code>ON DELETE NO ACTION</code>. Deletes fail loudly. Instead, rename the key (UPDATE PermissionKey) — FKs preserve grants because they're on the surrogate ID.</td>
              </tr>
              <tr>
                <td><strong>Never renumber a PermissionDefId.</strong></td>
                <td>The INT surrogate is referenced by DefRoles, UserPermissions, GlobalPermissions, TableOwner. Renumbering would orphan grants. Rename the <code>PermissionKey</code> string, not the ID.</td>
              </tr>
              <tr>
                <td><strong>Classify new tables before use.</strong></td>
                <td>A project-DB table with no <code>AppSec_TableOwner</code> row returns 403 from DynamicController with "Table X not classified — contact admin". Add the row via <strong>Administration &gt; Security &gt; Table Ownership</strong> before anyone tries CRUD.</td>
              </tr>
              <tr>
                <td><strong>Data lives in DB; nav lives in code.</strong></td>
                <td>Permission grants, role assignments, table ownership — data (AppSec_*). Menu structure, routes, guard components — code (<code>navigationConfig.js</code>, <code>App.js</code>). Don't try to move nav to DB — it's coupled to the React tree.</td>
              </tr>
              <tr>
                <td><strong>Report classification is JSON, not nav config.</strong></td>
                <td>Each report's JSON config has a <code>section</code> field. Moving a report between sections = edit JSON once. Both nav placement AND backend enforcement update in lockstep.</td>
              </tr>
              <tr>
                <td><strong>Impersonation is SysAdmin-only UI preview.</strong></td>
                <td>Backend API calls always carry the SysAdmin's real JWT. Impersonation swaps <code>currentUser</code> in React state so you can SEE what the target sees. Nothing they do is server-authoritatively "them."</td>
              </tr>
            </tbody>
          </table>

          {/* ── Where to make changes ──────────────────── */}
          <h2 className="stui-detail-section-header">Where to Make Changes</h2>
          <table className="stui-data-grid">
            <thead>
              <tr><th>Change</th><th>Where</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Add / edit / rename a permission key</td>
                <td><strong>Administration &gt; Security &gt; Permissions</strong> (admin UI). Or direct <code>AppSec_DefPermissions</code> UPDATE.</td>
              </tr>
              <tr>
                <td>Grant a permission to a role (all users in that role get it)</td>
                <td><strong>Administration &gt; Security &gt; Permission Defaults</strong> — flips <code>AppSec_DefRoles</code>. SysAdmin column is read-only (flag bypass).</td>
              </tr>
              <tr>
                <td>Assign a user to a database</td>
                <td><strong>Administration &gt; Users</strong> → pick user → Roles tab.</td>
              </tr>
              <tr>
                <td>Explicit per-user grant/deny (override the role default)</td>
                <td><strong>Administration &gt; Users</strong> → pick user → Permissions tab.</td>
              </tr>
              <tr>
                <td>Cross-DB grant (rare)</td>
                <td><strong>Administration &gt; Users</strong> → pick user → Global Permissions.</td>
              </tr>
              <tr>
                <td>Classify a new project-DB table</td>
                <td><strong>Administration &gt; Security &gt; Table Ownership</strong> (SysAdmin only).</td>
              </tr>
              <tr>
                <td>Change what appears in the nav</td>
                <td>Code — <code>pmd-frontend/src/config/navigationConfig.js</code>. Deploy required.</td>
              </tr>
              <tr>
                <td>Move a dynamic report between sections</td>
                <td>Edit the report's JSON config (<code>AppCfg_ModuleDynamic.Config</code>, key <code>section</code>). No deploy required — nav re-fetches on next load.</td>
              </tr>
            </tbody>
          </table>

          {/* ── Special cases + gotchas ────────────────── */}
          <h2 className="stui-detail-section-header">Special Cases + Gotchas</h2>
          <table className="stui-data-grid">
            <thead>
              <tr><th>Situation</th><th>Behavior</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>SysAdmin</td>
                <td>Bypasses every server-side check via the <code>IsSysAdmin</code> flag on <code>AppSec_Users</code>. No DefRoles rows for SysAdmin — the Permission Defaults page displays a synthetic always-Grant column so admins see the full picture.</td>
              </tr>
              <tr>
                <td>Bootstrap on <code>AppSec_TableOwner</code></td>
                <td>If the table has zero rows, DynamicController allows all CRUD (bootstrap fallback). The moment ONE row is inserted, the guardrail activates table-by-table. Don't half-populate on prod without care.</td>
              </tr>
              <tr>
                <td><code>engineering.supportingdoc</code> — two-key split</td>
                <td>Two permission keys govern the supporting-doc scope: <code>engineering.supportingdoc.view</code> (read-only) and <code>engineering.supportingdoc</code> (base — read + edit-eligible). Reads accept either; writes require the base. User role gets <code>.view</code> by default; PowerUser+ gets the base.</td>
              </tr>
              <tr>
                <td>Datasheet pencil vs. listview edit</td>
                <td>The datasheet action keys (<code>action.engineering.datasheet.update</code>, <code>action.dataentry.datasheet.update</code>) are UX gates on the pencil button in datasheet views. The backend still checks <code>action.&lt;category&gt;.listview.update</code> for the actual write — anyone who can edit via listview can theoretically loop single-row PUTs, so we don't over-gate the API. Datasheet key is about the pencil, not the API.</td>
              </tr>
              <tr>
                <td>Data Entry writes → engineering owner check</td>
                <td>Source, Commissioning_Key, and Transfer_Party live under Data Entry modules but are owned by <code>engineering.supportingdoc</code>. A user with <code>dataentry.estimating</code> alone can see the Estimating nav but can't write to Source without <code>engineering.supportingdoc</code> (base). Two grants required — module for nav, engineering owner for write.</td>
              </tr>
              <tr>
                <td>DB slug vs. DatabaseName</td>
                <td><code>X-Database-Id</code> header carries the DatabaseName ("pmd-preprod-db-gwa01"). Permissions and roles dicts are keyed by the Slug ("gwa01"). Translation happens server-side via <code>ResolvedAccess.SlugFor()</code>.</td>
              </tr>
              <tr>
                <td>Impersonation redirect</td>
                <td>When SysAdmin picks a user + DB in the impersonate popup, the app navigates to that DB context. If the impersonated user has no DB access, they land on the DB picker (which will show their empty state).</td>
              </tr>
            </tbody>
          </table>

          {/* ── Pointers ────────────────────────────── */}
          <h2 className="stui-detail-section-header">Where to Read More</h2>
          <ul>
            <li><code>dev-tracking/PMD-Claude-Notes.md</code> — full architecture reference.</li>
            <li><code>dev-tracking/sql/a20-permission-rework.sql</code> — canonical DefRoles rebuild for this model. Idempotent.</li>
            <li><code>PMD-API/PMD.API/Services/IPermissionChecker.cs</code> — server-side rule implementation. Read this to see the check logic.</li>
            <li><code>pmd-frontend/src/context/AuthContext.js</code> — frontend helpers (<code>canReadTable</code>, <code>canWriteTable</code>, <code>canEditDatasheet</code>, <code>canDeleteTable</code>, <code>canBulkDeleteTable</code>, <code>canDownloadTable</code>) that mirror the backend rules.</li>
            <li><strong>Rev Log</strong> (Development &gt; Rev Log) — commit-level history. a20.044 is the current model version.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SecurityOverviewPage;
